import React, { useState } from 'react';
import { TextField, Button, Box, Alert, InputAdornment, IconButton, useTheme, Zoom, Theme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { signup } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';
import { useLang } from '../../contexts/langContext';
import { useBranding } from '../../contexts/BrandingContext';
import { Visibility, VisibilityOff, Email, Lock, Person } from '@mui/icons-material';
import { getStrongPasswordErrorMessage, isStrongPassword } from '../../utils/password';

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
}

interface Props {
  isAdminInitMode?: boolean;
}

function SignupForm({ isAdminInitMode = false }: Props) {
  const { lang } = useLang();
  const { requireStrongPassword } = useBranding();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setIsSubmitting(true);

    // Basic client-side validation
    const newFieldErrors: FieldErrors = {};
    if (!name) newFieldErrors.name = lang === 'en' ? 'Name is required' : '姓名是必填项';
    if (!email) newFieldErrors.email = lang === 'en' ? 'Email is required' : '邮箱是必填项';
    if (!password) newFieldErrors.password = lang === 'en' ? 'Password is required' : '密码是必填项';
    if (requireStrongPassword || isAdminInitMode) {
      if (password && !isStrongPassword(password)) {
        newFieldErrors.password = getStrongPasswordErrorMessage(lang);
      }
    } else if (password.length < 6) {
      newFieldErrors.password = lang === 'en' ? 'Password must be at least 6 characters' : '密码至少6个字符';
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const user = await signup(name, email, password);
      setUser(user);
      navigate('/projects');
    } catch (error: any) {
      if (error.response && error.response.data) {
        if (error.response.data.errorCode) {
          // Handle field-specific errors
          switch (error.response.data.errorCode) {
            case 'invalid_email':
              setFieldErrors({ email: lang === 'en' ? 'Invalid email address' : '无效的邮箱地址' });
              break;
            case 'email_already_exists':
              setFieldErrors({ email: lang === 'en' ? 'Email already in use' : '邮箱已被使用' });
              break;
            case 'password_too_short':
              setFieldErrors({ password: lang === 'en' ? 'Password must be at least 6 characters' : '密码至少6个字符' });
              break;
            case 'invalid_credentials':
              setFormError(lang === 'en' ? 'Invalid credentials' : '无效的凭证');
              break;
            default:
              setFormError(error.response.data.message || (lang === 'en' ? 'An error occurred during signup' : '注册时发生错误'));
          }
        } else {
          setFormError(error.response.data.message || (lang === 'en' ? 'An error occurred during signup' : '注册时发生错误'));
        }
      } else {
        setFormError(lang === 'en' ? 'An unexpected error occurred' : '发生了一个未知的错误');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const fieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(8px)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      },
      '&.Mui-focused': {
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      },
    },
    '& .MuiInputLabel-root': {
      color: (theme: Theme) => theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (theme: Theme) => theme.palette.primary.main,
    },
    mb: 1,
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        mt: 1,
        '& .MuiTextField-root': {
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {},
        },
      }}
    >
      {formError && (
        <Zoom in>
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            {formError}
          </Alert>
        </Zoom>
      )}
      <TextField
        margin="normal"
        required
        fullWidth
        id="name"
        label={lang === 'en' ? 'Full Name' : '姓名'}
        name="name"
        autoComplete="name"
        autoFocus
        size="small"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={!!fieldErrors.name}
        helperText={fieldErrors.name}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person sx={{ color: (theme: Theme) => theme.palette.primary.main, fontSize: '1.2rem' }} />
            </InputAdornment>
          ),
        }}
        sx={fieldStyle}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label={lang === 'en' ? 'Email Address' : '邮箱地址'}
        name="email"
        autoComplete="email"
        size="small"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!fieldErrors.email}
        helperText={fieldErrors.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email sx={{ color: (theme: Theme) => theme.palette.primary.main, fontSize: '1.2rem' }} />
            </InputAdornment>
          ),
        }}
        sx={fieldStyle}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label={lang === 'en' ? 'Password' : '密码'}
        type={showPassword ? 'text' : 'password'}
        id="password"
        size="small"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!fieldErrors.password}
        helperText={fieldErrors.password}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock sx={{ color: (theme: Theme) => theme.palette.primary.main, fontSize: '1.2rem' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                edge="end"
                size="small"
                sx={{
                  color: (theme: Theme) => theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              >
                {showPassword ? <VisibilityOff sx={{ fontSize: '1.2rem' }} /> : <Visibility sx={{ fontSize: '1.2rem' }} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={fieldStyle}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{
          mt: 3,
          py: 1,
          borderRadius: 2,
          fontSize: '0.9rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          background: (theme: Theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            background: (theme: Theme) => `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            background: (theme: Theme) => theme.palette.action.disabledBackground,
          },
        }}
      >
        {isSubmitting ? (lang === 'en' ? 'Signing up...' : '注册中...') : lang === 'en' ? 'Sign Up' : '注册'}
      </Button>
    </Box>
  );
}

export default SignupForm;